/**
 * Smart Shikka AI Core — PR93
 * Server-only Provider Settings + Secret Reference Adapter
 * Registry — Deep Frozen — No Provider Call — No Secret Literals
 */
function deepFreeze(obj) {
    if (obj && typeof obj === "object") {
        Object.freeze(obj);
        for (const key of Object.keys(obj)) {
            const val = obj[key];
            if (val && typeof val === "object" && !Object.isFrozen(val)) {
                deepFreeze(val);
            }
        }
    }
    return obj;
}
const BLOCKED_ACTIONS = [
    "anthropic_call", "child_process", "gemini_call",
    "model_inference", "old_assistant_alias", "openai_call",
    "provider_call", "secret_literal", "secret_read",
    "shell_command", "unapproved_db_write", "unapproved_file_write",
    "unapproved_network_call", "unknown_provider_call", "unknown_tool_execution",
    "unredacted_secret", "unsafe_env_dump",
    "runtime_action_execute", "runtime_unlock", "tool_execution",
    "agent_execution", "workflow_execution", "gate_unlock",
    "file_write", "db_write", "supabase_call",
    "browser_action", "desktop_action", "network_call",
    "chat_runtime_start", "message_send", "render_document",
    "inject_icon_runtime",
];
const COMMON_SAFETY = {
    previewOnly: true,
    readonlyOnly: true,
    providerCallAllowedInPr93: false,
    secretLiteralAllowedInPr93: false,
    secretReadAllowedInPr93: false,
    openaiCallAllowedInPr93: false,
    anthropicCallAllowedInPr93: false,
    geminiCallAllowedInPr93: false,
    modelInferenceAllowedInPr93: false,
    runtimeActionAllowedInPr93: false,
    runtimeUnlockAllowedInPr93: false,
    toolExecutionAllowedInPr93: false,
    agentExecutionAllowedInPr93: false,
    workflowExecutionAllowedInPr93: false,
    messageSendAllowedInPr93: false,
    chatRuntimeAllowedInPr93: false,
    documentRenderAllowedInPr93: false,
    iconInjectionAllowedInPr93: false,
    browserAutomationAllowedInPr93: false,
    desktopAutomationAllowedInPr93: false,
    fileMutationAllowedInPr93: false,
    networkAllowedInPr93: false,
    dbWriteAllowedInPr93: false,
    supabaseAllowedInPr93: false,
    gateUnlockAllowedInPr93: false,
    requiresOwnerApproval: true,
    requiresUserApproval: true,
};
const registry = [
    {
        id: "provider_settings_reference_summary",
        displayName: "Provider Settings Reference Summary",
        description: "Server-only provider setting metadata summary. No secret literals, no provider calls.",
        sourceModules: ["smart-shikka-assistant-orchestrator", "smart-shikka-assistant-no-runtime-foundation-handoff"],
        targetPreviewModules: ["smart-shikka-assistant-provider-settings-secret-reference"],
        relatedRouteIds: ["capability_discovery_request", "safety_guardrail_explain_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card", "safety_guardrail_explain_card"],
        relatedChatPreviewIds: ["provider_call_blocked_card"],
        relatedConversationStatePreviewIds: ["readonly_blocked_runtime_state"],
        relatedApprovalStatePreviewIds: ["readonly_provider_call_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_provider_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Provider settings are reference metadata only", "No secret literals in PR93", "No provider call in PR93"],
        futurePrScope: ["Provider healthcheck in future PR", "Provider call gate unlock"],
        notes: ["Server-only metadata — no client-side exposure"],
    },
    {
        id: "openai_provider_reference",
        displayName: "OpenAI Provider Reference",
        description: "Reference metadata for OpenAI provider configuration. Secret key referenced by ID only, never stored as literal.",
        sourceModules: ["smart-shikka-assistant-provider-settings-secret-reference"],
        targetPreviewModules: ["smart-shikka-assistant-provider-settings-secret-reference"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: ["provider_call_blocked_card"],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: ["readonly_provider_call_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_provider_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["OpenAI call not allowed in PR93", "Secret referenced by ID only"],
        futurePrScope: ["OpenAI provider call gate unlock"],
        notes: ["Secret reference ID: OPENAI_API_KEY_REF", "No literal value stored"],
    },
    {
        id: "anthropic_provider_reference",
        displayName: "Anthropic Provider Reference",
        description: "Reference metadata for Anthropic provider configuration. Secret key referenced by ID only.",
        sourceModules: ["smart-shikka-assistant-provider-settings-secret-reference"],
        targetPreviewModules: ["smart-shikka-assistant-provider-settings-secret-reference"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: ["provider_call_blocked_card"],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: ["readonly_provider_call_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_provider_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Anthropic call not allowed in PR93", "Secret referenced by ID only"],
        futurePrScope: ["Anthropic provider call gate unlock"],
        notes: ["Secret reference ID: ANTHROPIC_API_KEY_REF", "No literal value stored"],
    },
    {
        id: "gemini_provider_reference",
        displayName: "Gemini Provider Reference",
        description: "Reference metadata for Gemini provider configuration. Secret key referenced by ID only.",
        sourceModules: ["smart-shikka-assistant-provider-settings-secret-reference"],
        targetPreviewModules: ["smart-shikka-assistant-provider-settings-secret-reference"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: ["provider_call_blocked_card"],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: ["readonly_provider_call_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_provider_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Gemini call not allowed in PR93", "Secret referenced by ID only"],
        futurePrScope: ["Gemini provider call gate unlock"],
        notes: ["Secret reference ID: GEMINI_API_KEY_REF", "No literal value stored"],
    },
    {
        id: "provider_secret_reference_placeholder",
        displayName: "Provider Secret Reference Placeholder",
        description: "Placeholder for secret reference adapter. References secrets by ID; never stores or reads literal values.",
        sourceModules: ["smart-shikka-assistant-provider-settings-secret-reference"],
        targetPreviewModules: ["smart-shikka-assistant-provider-settings-secret-reference"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Secret adapter is reference-only placeholder", "No secret literal storage", "No secret read in PR93"],
        futurePrScope: ["Secret adapter live wiring in future PR"],
        notes: ["All secrets referenced by ID only", "Redaction policy applies"],
    },
    {
        id: "provider_budget_limit_placeholder",
        displayName: "Provider Budget Limit Placeholder",
        description: "Placeholder for per-provider budget limit metadata. No actual enforcement in PR93.",
        sourceModules: ["smart-shikka-assistant-provider-settings-secret-reference"],
        targetPreviewModules: ["smart-shikka-assistant-provider-settings-secret-reference"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Budget limit is placeholder metadata only", "No enforcement in PR93"],
        futurePrScope: ["Budget enforcement in future PR"],
        notes: ["Per-provider budget limits defined as metadata"],
    },
    {
        id: "provider_region_policy_placeholder",
        displayName: "Provider Region Policy Placeholder",
        description: "Placeholder for region policy metadata. Defines data residency preferences per provider.",
        sourceModules: ["smart-shikka-assistant-provider-settings-secret-reference"],
        targetPreviewModules: ["smart-shikka-assistant-provider-settings-secret-reference"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Region policy is placeholder metadata only", "No enforcement in PR93"],
        futurePrScope: ["Region enforcement in future PR"],
        notes: ["Data residency preferences per provider"],
    },
    {
        id: "provider_redaction_policy",
        displayName: "Provider Redaction Policy",
        description: "Redaction policy for provider secrets. All secret references must be redacted in logs, audit trails, and reports.",
        sourceModules: ["smart-shikka-assistant-provider-settings-secret-reference"],
        targetPreviewModules: ["smart-shikka-assistant-provider-settings-secret-reference"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Redaction policy is metadata-only declaration", "No secret access in PR93"],
        futurePrScope: ["Live redaction enforcement in future PR"],
        notes: ["All secret references redacted as [REDACTED_SECRET_REF]", "No literal values in audit logs"],
    },
    {
        id: "provider_call_disabled_gate",
        displayName: "Provider Call Disabled Gate",
        description: "Gate entry confirming provider calls are disabled in PR93. Must be unlocked per-provider in future PRs.",
        sourceModules: [
            "smart-shikka-assistant-readonly-execution-gate-matrix-preview",
            "smart-shikka-assistant-provider-settings-secret-reference",
        ],
        targetPreviewModules: ["smart-shikka-assistant-provider-settings-secret-reference"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: ["provider_call_blocked_card"],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: ["readonly_provider_call_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_provider_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Provider call gate is locked", "No provider call in PR93", "Per-provider unlock required"],
        futurePrScope: ["Per-provider gate unlock with dual approval"],
        notes: ["Gate locked for all providers", "Requires owner + user approval per unlock"],
    },
    {
        id: "future_provider_healthcheck_ready",
        displayName: "Future Provider Healthcheck Ready",
        description: "Placeholder for future provider healthcheck capability. No healthcheck executed in PR93.",
        sourceModules: ["smart-shikka-assistant-provider-settings-secret-reference"],
        targetPreviewModules: [],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Healthcheck is future scope only", "No provider call in PR93"],
        futurePrScope: ["Provider healthcheck in future PR", "Requires provider call gate unlock first"],
        notes: ["Healthcheck defined as metadata only", "No network call in PR93"],
    },
];
deepFreeze(registry);
export function getSmartShikkaAssistantProviderSettingsSecretReferenceEntries() {
    return registry;
}
export function getSmartShikkaAssistantProviderSettingsSecretReferenceEntryById(id) {
    return registry.find((e) => e.id === id);
}
//# sourceMappingURL=smartShikkaAssistantProviderSettingsSecretReferenceRegistry.js.map